import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuthContext } from '../hooks/useAuthContext';
import API from '../../../config/API';

const USER_TYPE_MAP = {
  Student_Account: 0,
  Teacher_Account: 1,
  Organization_Account: 2,
};

const normalizeToken = (token) => {
  try {
    return decodeURIComponent(token || '').trim();
  } catch (error) {
    return String(token || '').trim();
  }
};

const mapUserType = (value) => {
  if (typeof value === 'number') {
    return value;
  }

  const normalized = String(value || '').trim();
  if (normalized === '0' || normalized === '1' || normalized === '2') {
    return Number(normalized);
  }

  return USER_TYPE_MAP[normalized] ?? 0;
};

const getPayload = (data) => {
  if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    return data.data;
  }

  return data || {};
};

const getTeacherId = (sessionInfo) => {
  const payload = getPayload(sessionInfo);
  const teacherId = payload.teacher_id || payload.tutor_id || payload.teacher_user_id || 0;

  return Array.isArray(teacherId) ? teacherId[0] || 0 : teacherId;
};

const buildUserData = (responseData, token) => {
  const payload = getPayload(responseData);
  const user = payload.user || responseData?.user || {};
  const userId = user.id || user.user_id || user.uid;

  if (!userId) {
    throw new Error('Authenticated user data is missing an id');
  }

  return {
    id: userId,
    full_name: user.full_name || user.name || user.display_name || 'User',
    token,
    user_type: mapUserType(user.user_type ?? user.who_are_you),
    who_are_you: user.who_are_you || null,
    img_path: user.img_path || user.image || null,
    email: user.email || user.login || '',
    is_guest: false,
  };
};

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  const apiMessage = error?.response?.data?.message;

  if (status === 401 || status === 403) {
    return apiMessage || 'Your account is not allowed to join this session.';
  }

  if (status === 404) {
    return apiMessage || 'This session could not be found.';
  }

  if (apiMessage) {
    return apiMessage;
  }

  return 'Unable to join this session right now. Please try again.';
};

const AttendSession = () => {
  const navigate = useNavigate();
  const { session_id, token } = useParams();
  const { user, dispatch } = useAuthContext();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const startedForRef = useRef(null);

  useEffect(() => {
    const sessionId = Number(session_id);
    const normalizedToken = normalizeToken(token);
    const handoffKey = `${sessionId}:${normalizedToken}`;

    if (startedForRef.current === handoffKey) {
      return;
    }

    startedForRef.current = handoffKey;

    const getSessionInfo = async () => {
      const response = await API.get(`session/info/${sessionId}`);
      return response.data;
    };

    const validateToken = async () => {
      if (user?.token === normalizedToken && user?.id) {
        return user;
      }

      const response = await API.get('auth/validate-token', {
        headers: {
          Authorization: `Bearer ${normalizedToken}`,
        },
      });

      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || 'Invalid session token');
      }

      return buildUserData(response.data, normalizedToken);
    };

    const checkClassroomAccess = async () => {
      const response = await API.get(`check_user_access_class_room?sess_id=${sessionId}`, {
        headers: {
          Authorization: normalizedToken,
          'Content-Type': 'application/json',
        },
      });

      if (response.data?.status && response.data.status !== 'success') {
        throw new Error(response.data?.message || 'Session access was denied');
      }

      return response.data;
    };

    const authenticateAndJoin = async () => {
      if (!sessionId || !normalizedToken) {
        setError('Missing session ID or token.');
        setLoading(false);
        return;
      }

      try {
        const [validatedUser, sessionInfo] = await Promise.all([
          validateToken(),
          getSessionInfo(),
        ]);

        await checkClassroomAccess();

        localStorage.setItem('user', JSON.stringify(validatedUser));
        dispatch({ type: 'LOGIN', payload: validatedUser });

        navigate(`/class_room/${sessionId}/${getTeacherId(sessionInfo)}`, { replace: true });
      } catch (err) {
        console.error('Session SSO failed:', err);
        setError(getErrorMessage(err));
        setLoading(false);
      }
    };

    authenticateAndJoin();
  }, [session_id, token, user, dispatch, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white', mb: 3 }} />
        <Typography variant="h5" sx={{ color: 'white', mb: 1 }}>
          Joining Session...
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Please wait while we authenticate you
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 480 }}>
          {error}
        </Alert>
        <Typography variant="body1" sx={{ color: 'white' }}>
          Please return to the course page and try again.
        </Typography>
      </Box>
    );
  }

  return null;
};

export default AttendSession;
